/**
 * Wellness "brain" formatting — display rules for the deeper read the live
 * snapshot carries (payload.brain): per-vital personal baselines and today's
 * named medications. Same contract as wellnessFormat.ts: plain functions, no
 * React, so the watch bridge or any headless consumer can reuse them and jest
 * covers them without dragging component module graphs along.
 *
 * The server computes truth (values, p25–p75 bands, typical/above/below);
 * this module only renders it. Copy is deliberately neutral about the person
 * ("above usual", never a pronoun) — recipient pronouns aren't collected.
 */

export type WellnessBodySignal = {
  key: string;
  label: string;
  unit: string;
  kind: 'number' | 'oxygen' | 'sleep_minutes' | 'steps'
    // Hand-recorded on the check-in sheet.
    | 'blood_pressure' | 'score' | 'count' | 'yes_no';
  /** Upper bound for `score` kinds — pain is out of 10, mood out of 5. */
  scale?: number;
  latest: {
    value: number;
    at: string | null;
    unit: string | null;
    source?: string | null;
    /** Diastolic, on a blood-pressure row. 120 says nothing without the 80. */
    secondary?: number | null;
  } | null;
  baseline: { p25: number; p75: number; median: number; days: number } | null;
  /** Whole hours since the reading was taken; null when the server couldn't tell. */
  age_hours?: number | null;
  /** How old this particular vital may be and still describe the present. */
  fresh_hours?: number;
  /** What has to be in play for it to refresh. */
  needs?: 'watch' | 'phone';
  stale?: boolean;
  state: 'typical' | 'above' | 'below' | 'unknown' | 'stale';
};

export type WellnessMedRow = {
  id: number | string;
  name: string;
  dose: string | null;
  taken: boolean;
  taken_at: string | null;
};

export type WellnessMedsToday = {
  meds: WellnessMedRow[];
  total: number;
  taken_count: number;
};

export type WellnessBrain = {
  body_signals?: WellnessBodySignal[];
  /** The subjective half of the check-in sheet — pain, mood, energy, appetite. */
  checkin_signals?: WellnessBodySignal[];
  meds_today?: WellnessMedsToday;
};

/** Chip meta per classification. Amber for both directions — "above" isn't
 *  worse than "below"; both just mean "outside the personal band, look".
 *
 *  `stale` is grey and says so outright. It used to be impossible to reach:
 *  a reading from April was classified against the band exactly like one from
 *  eight minutes ago, so a 102-day-old resting heart rate carried the same
 *  green "usual range" chip as a live one. Green on a stale row is not a
 *  neutral default — it is a reassurance nobody checked. */
export const SIGNAL_STATE_META: Record<WellnessBodySignal['state'], { label: string; color: string }> = {
  typical: { label: 'usual range', color: '#10B981' },
  above: { label: 'above usual', color: '#F59E0B' },
  below: { label: 'below usual', color: '#F59E0B' },
  unknown: { label: 'learning baseline', color: '#6B7280' },
  stale: { label: 'not current', color: '#6B7280' },
};

/** The chip for one row. `unknown` usually means too little history, but a row
 *  with both a baseline and a reading got there as a day count still under way,
 *  which the server won't judge against whole days yet (services/wellness/brain). */
export function signalChip(sig: WellnessBodySignal): { label: string; color: string } {
  const meta = SIGNAL_STATE_META[sig.state] || SIGNAL_STATE_META.unknown;
  return sig.state === 'unknown' && sig.baseline && sig.latest ? { ...meta, label: 'so far today' } : meta;
}

/** Past this, a duration stops being something anyone reasons about and an
 *  absolute date is simply more useful. "measured 102d ago" is arithmetic
 *  homework; "measured 30 Apr" is a fact. */
const ABSOLUTE_DATE_AFTER_HOURS = 7 * 24;

/**
 * When the reading was taken — "6h ago" up close, a plain date once it's old.
 * Returns '' rather than guessing when there's no usable timestamp: no line at
 * all beats an invented one.
 */
export function measuredAt(iso: string | null | undefined, nowMs: number = Date.now()): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const hours = Math.max(0, Math.floor((nowMs - t) / 3_600_000));
  if (hours < ABSOLUTE_DATE_AFTER_HOURS) {
    const mins = Math.max(0, Math.floor((nowMs - t) / 60_000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }
  const d = new Date(t);
  const sameYear = d.getFullYear() === new Date(nowMs).getFullYear();
  try {
    return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      ...(sameYear ? {} : { year: 'numeric' }),
    });
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/**
 * The line a stale row shows INSTEAD of "usually 69–76 bpm".
 *
 * Naming the device is the actually useful thing — it says what to put back on.
 * Provenance only exists on readings synced by a client new enough to record
 * it, which is precisely the wrong half for old readings, so there's a generic
 * fallback. Deliberately not "Apple Watch": on Android that would be a lie, and
 * when we DO know the device we print its real name anyway.
 */
export function signalStaleNote(sig: WellnessBodySignal): string | null {
  if (!sig.stale) return null;
  if (sig.latest?.source) return `last from ${sig.latest.source}`;
  return sig.needs === 'phone' ? 'needs a phone sync' : 'needs a paired watch';
}

function minutesToHm(mins: number): string {
  const total = Math.round(mins);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h <= 0) return `${m}m`;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** HealthKit ships oxygen as a 0–1 fraction; some sources send 0–100. */
function oxygenPct(value: number): number {
  return value <= 1 ? value * 100 : value;
}

function trimNumber(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function formatByKind(kind: WellnessBodySignal['kind'], value: number, unit: string): string {
  switch (kind) {
    case 'sleep_minutes':
      return minutesToHm(value);
    case 'oxygen':
      return `${trimNumber(oxygenPct(value))}%`;
    case 'steps':
    case 'count':
      return unit
        ? `${Math.round(value).toLocaleString()} ${unit}`
        : Math.round(value).toLocaleString();
    default:
      return unit ? `${trimNumber(value)} ${unit}` : trimNumber(value);
  }
}

/** Latest reading, display-ready ("62 bpm", "7h 20m", "97%", "120/80"), or null. */
export function formatSignalValue(sig: WellnessBodySignal): string | null {
  if (!sig.latest || sig.latest.value == null) return null;

  if (sig.kind === 'blood_pressure') {
    // Never render a lone systolic. "120 mmHg" is not a blood pressure, and a
    // reader who takes it for one has been told something false about it.
    const dia = sig.latest.secondary;
    if (dia == null) return null;
    return `${Math.round(sig.latest.value)}/${Math.round(dia)}`;
  }
  if (sig.kind === 'yes_no') return sig.latest.value >= 1 ? 'Yes' : 'No';
  if (sig.kind === 'score') {
    // Out of its own scale: "3" means very different things on a 0–10 pain
    // scale and a 1–5 mood one, and the reader shouldn't have to remember which.
    return sig.scale ? `${trimNumber(sig.latest.value)}/${sig.scale}` : trimNumber(sig.latest.value);
  }
  return formatByKind(sig.kind, sig.latest.value, sig.unit);
}

/** The personal band as copy: "usually 58–66 bpm", "usually 6h 50m–8h 10m". */
export function formatSignalBaseline(sig: WellnessBodySignal): string | null {
  const b = sig.baseline;
  if (!b) return null;
  switch (sig.kind) {
    case 'blood_pressure':
      // The band is systolic only — diastolic is a separate stored type with
      // its own history. Say which number it describes rather than letting it
      // read as a range for the pair.
      return `usually ${Math.round(b.p25)}–${Math.round(b.p75)} systolic`;
    case 'score':
      return `usually ${trimNumber(b.p25)}–${trimNumber(b.p75)}${sig.scale ? ` of ${sig.scale}` : ''}`;
    case 'count':
      return `usually ${Math.round(b.p25)}–${Math.round(b.p75)}${sig.unit ? ` ${sig.unit}` : ''}`;
    case 'yes_no':
      return null;
    case 'sleep_minutes':
      return `usually ${minutesToHm(b.p25)}–${minutesToHm(b.p75)}`;
    case 'oxygen':
      return `usually ${trimNumber(oxygenPct(b.p25))}–${trimNumber(oxygenPct(b.p75))}%`;
    case 'steps':
      return `usually ${Math.round(b.p25).toLocaleString()}–${Math.round(b.p75).toLocaleString()}`;
    default: {
      const range = `${trimNumber(b.p25)}–${trimNumber(b.p75)}`;
      return sig.unit ? `usually ${range} ${sig.unit}` : `usually ${range}`;
    }
  }
}
